import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { useFilters } from "@/context/FiltersContext";
import { ENTRY, FLOOR, PHONE, TEL, useCafe } from "@/lib/cafe";
import { track } from "@/lib/track";

export const Wordmark = ({ className = "h-9 sm:h-10", white = false }) => (
  <img src={`/brand/zone6-logo-${white ? "white" : "marine"}.svg`} alt="Zone 6 by Biopeak" data-testid="brand-logo" width={978} height={645}
    className={`block w-auto select-none ${className}`} draggable={false} />
);

const NavTab = ({ to, end, testId, children }) => (
  <NavLink to={to} end={end} data-testid={testId} className={({ isActive }) => `font-ui relative inline-flex min-h-[48px] items-center whitespace-nowrap px-1 text-[15px] uppercase tracking-[0.14em] text-[#0B2E44] landscape:md:tracking-[0.2em] min-[1180px]:tracking-[0.28em] ${isActive ? "font-normal" : "font-normal"}`}>
    {({ isActive }) => (
      <>
        {children}
        <span aria-hidden className={`absolute inset-x-1 bottom-1.5 h-[2px] origin-left rounded-full bg-[#E8508A] transition-transform duration-300 ${isActive ? "scale-x-100" : "scale-x-0"}`} />
      </>
    )}
  </NavLink>
);

const Tabs = ({ onPick }) => (
  <>
    {[["/", "Home", "home"], ["/menu", "Menus", "menus"], ["/menu/all", "Full menu", "full-menu"], ["/beverages", "Beverages", "beverages"], ["/about", "About us", "about"]].map(([to, label, k], i) => (
      <span key={k} className="contents" onClickCapture={onPick}>
        {i > 0 && !onPick && <span aria-hidden className="hidden text-[#E8508A] min-[1180px]:inline">·</span>}
        <NavTab to={to} end={to === "/" || to === "/menu" || to === "/menu/all"} testId={`nav-${k}-link${onPick ? "-mobile" : ""}`}>{label}</NavTab>
      </span>
    ))}
  </>
);

const BiopeakLink = () => (
  <a href="https://biopeak.com" target="_blank" rel="noreferrer" data-testid="nav-biopeak-btn"
    className="z6-btn z6-btn--sm shrink-0 whitespace-nowrap">
    <span className="sr-only">Go to </span>
    <img src="/brand/biopeak-wordmark.svg" alt="Biopeak" width={80} height={14} className="block h-[13px] w-auto sm:h-[14px]" draggable={false} />
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-[#E8508A]" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M7 17L17 7M9 7h8v8" /></svg>
  </a>
);

const POLICIES = [["Refund & Cancellation", "https://biopeak.com/refund-and-cancellation", "refund"], ["Terms & Conditions", "https://biopeak.com/terms-and-conditions", "terms"], ["Privacy Policy", "https://biopeak.com/privacy-policy", "privacy"]];
const PolicyLinks = () => (
  <nav data-testid="footer-policies" className="mt-5 flex flex-wrap gap-x-5 gap-y-1">
    {POLICIES.map(([l, href, k]) => (
      <a key={k} href={href} target="_blank" rel="noreferrer" data-testid={`footer-policy-${k}`} className="font-ui inline-flex min-h-[44px] items-center text-[14px] text-[#33566B] underline-offset-4 hover:text-[#B8326C] hover:underline">{l}</a>
    ))}
  </nav>
);

export const TopBar = ({ onOpenFilters }) => {
  const { activeCount } = useFilters();
  const [open, setOpen] = useState(false);
  const filter = onOpenFilters && (
    <motion.button whileTap={{ scale: 0.94 }} data-testid="filter-panel-trigger" onClick={onOpenFilters}
      aria-pressed={activeCount > 0} className="z6-btn z6-btn--sm bg-white/70">
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></svg>
      <span className="hidden min-[1100px]:inline">Refine</span>
      {activeCount > 0 && (
        <span data-testid="filter-active-count" className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-transparent ring-1 ring-[#E8508A] hover:bg-[#E8508A]/10 px-1.5 font-mono text-[16px] font-normal text-[#B8326C]">{activeCount}</span>
      )}
    </motion.button>
  );
  return (
    <header className="sticky top-0 z-40">
      <div className="glass-bar mx-auto flex max-w-[1400px] items-center justify-between gap-3 px-4 py-2 sm:px-8">
        <Link to="/" data-testid="header-brand-link" className="flex min-h-[48px] shrink-0 items-center"><Wordmark className="h-[52px] sm:h-[68px] [@media(max-height:500px)]:h-[38px]" /></Link>
        <nav className="hidden min-w-0 items-center gap-2 lg:flex landscape:lg:gap-4 min-[1180px]:gap-6"><Tabs />{filter}<BiopeakLink /></nav>
        <div className="flex items-center gap-2 lg:hidden">
          {filter}
          <BiopeakLink />
          <button type="button" data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen((o) => !o)}
            className="flex h-12 w-12 items-center justify-center rounded-[999px_999px_999px_3px] text-[#0B2E44] ring-1 ring-[#0B2E44]/30">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6">{open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 8h16M4 16h16" />}</svg>
          </button>
        </div>
      </div>
      {open && (
        <nav data-testid="nav-mobile-panel" className="glass-bar flex flex-col items-start gap-1 px-5 pb-3 lg:hidden"><Tabs onPick={() => setOpen(false)} /></nav>
      )}
    </header>
  );
};

export const OrderLinks = ({ className = "" }) => {
  const c = useCafe();
  const links = [["zomato", "Order on Zomato", c.zomato_url], ["swiggy", "Order on Swiggy", c.swiggy_url]].filter((l) => l[2]);
  if (!links.length) return null;
  return (
    <div data-testid="order-links" className={`flex flex-wrap gap-3 ${className}`}>
      {links.map(([k, label, url]) => (
        <a key={k} href={url} target="_blank" rel="noreferrer" data-testid={`order-${k}-btn`} onClick={() => track(`${k}_clicked`)}
          className="font-ui inline-flex min-h-[48px] items-center rounded-full bg-white px-5 text-[16px] font-normal text-[#0B2E44] ring-1 ring-[#0B5C8A]/30 active:ring-[#F08DB5]">{label}</a>
      ))}
    </div>
  );
};

// The one shared "Visit us" block: phone, floor, address, directions, entry note.
const VisitUs = () => {
  const c = useCafe();
  return (
    <section data-testid="footer-visit" className="mb-8 grid gap-4 border-t border-[#0B2E44]/15 pt-6 sm:grid-cols-[auto_1fr] sm:gap-10">
      <h2 className="font-display text-3xl text-[#0B2E44]">Visit <span className="italic">us</span></h2>
      <div className="font-ui space-y-2 text-[16px] text-[#0B2E44]">
        <p data-testid="footer-floor">{FLOOR}{c.address ? ` · ${c.address}` : ""}</p>
        <p data-testid="footer-entry" className="text-[#33566B]">{ENTRY}</p>
        <div className="flex flex-wrap gap-3 pt-2">
          <a href={TEL} data-testid="footer-call" onClick={() => track("call_clicked")} className="z6-btn z6-btn--pink">Call <em>{PHONE}</em></a>
          {c.maps_url && <a href={c.maps_url} target="_blank" rel="noreferrer" data-testid="footer-directions" onClick={() => track("directions_clicked")} className="z6-btn">Directions<span aria-hidden className="z6-arrow" /></a>}
        </div>
      </div>
    </section>
  );
};

export const Footer = ({ orderLinks = false }) => (
  <footer className="relative z-10 mx-auto max-w-[1400px] px-5 pb-28 pt-16 sm:px-8">
    {orderLinks && <OrderLinks className="mb-6" />}
    <VisitUs />
    <div className="flex flex-col gap-3 border-t border-[#0B2E44]/15 pt-6 sm:flex-row sm:items-center sm:justify-between">
      <p data-testid="footer-disclaimer" className="font-ui text-[15px] leading-relaxed text-[#0B2E44]">
        Menu information is for guidance and is not medical advice. Please inform your server of any allergies.
        <span data-testid="footer-tax-note" className="mt-1 block">Govt. taxes as applicable.</span>
      </p>
      <div className="flex items-center gap-5">
        <Link to="/staff" data-testid="footer-staff-login" className="font-ui inline-flex min-h-[48px] items-center text-[15px] text-[#0B2E44] underline-offset-4 hover:underline">Staff login</Link>
      </div>
    </div>
    <PolicyLinks />
  </footer>
);

export const HaraOrb = ({ className = "" }) => (
  <div aria-hidden className={`hara-orb pointer-events-none ${className}`}>
    <span className="hara-orb-dash" />
    <span className="hara-orb-core" />
    <span className="hara-orb-ring" />
    <span className="hara-orb-ring hara-orb-ring-2" />
  </div>
);
